import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Box, Typography, Fade } from '@mui/material';

const useStyles = makeStyles()((theme) => ({
  loaderContainerFullPage: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100vw',
    height: '100vh',
    position: 'fixed',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)',
    backdropFilter: 'blur(12px)',
    top: 0,
    left: 0,
    zIndex: 9999,
    overflow: 'hidden'
  },

  loaderContainerOverlay: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    position: 'absolute',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.90) 100%)',
    backdropFilter: 'blur(8px)',
    top: 0,
    left: 0,
    zIndex: 1000,
    borderRadius: 'inherit'
  },

  loaderContainerSection: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: '300px',
    position: 'relative',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.90) 100%)',
    borderRadius: '12px',
    border: '1px solid rgba(74, 144, 226, 0.1)',
    backdropFilter: 'blur(4px)',
    padding: theme.spacing(4)
  },

  loaderContainerCompact: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    minHeight: '150px',
    position: 'relative',
    background: 'rgba(248,250,252,0.8)',
    borderRadius: '8px',
    padding: theme.spacing(2)
  },

  loaderContainerInline: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    padding: theme.spacing(2),
    gap: theme.spacing(1.5)
  },

  loaderContainerCentered: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '60vh',
    position: 'relative'
  },

  warehouseScene: {
    position: 'relative',
    perspective: '400px',
    marginBottom: theme.spacing(3)
  },

  warehouseSceneLarge: { width: '140px', height: '100px' },
  warehouseSceneMedium: { width: '100px', height: '70px' },
  warehouseSceneSmall: { width: '70px', height: '50px' },
  warehouseSceneMini: { width: '50px', height: '35px' },

  warehouse3D: {
    position: 'relative',
    width: '100%',
    height: '100%',
    transformStyle: 'preserve-3d',
    animation: 'warehouseFloat 4s ease-in-out infinite'
  },

  warehouseBox: {
    position: 'absolute',
    background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
    borderRadius: '4px',
    boxShadow: '0 8px 16px rgba(44, 62, 80, 0.3)',
    animation: 'boxStack 3s ease-in-out infinite',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-3px',
      left: '3px',
      background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
      borderRadius: '3px',
      transform: 'rotateX(90deg)',
      transformOrigin: 'bottom',
      boxShadow: '0 2px 4px rgba(74, 144, 226, 0.3)'
    },

    '&::after': {
      content: '""',
      position: 'absolute',
      top: '3px',
      right: '-3px',
      background: 'linear-gradient(135deg, #2E5BBA 0%, #1E3F7A 100%)',
      borderRadius: '3px',
      transform: 'rotateY(90deg)',
      transformOrigin: 'left',
      boxShadow: '2px 0 4px rgba(46, 91, 186, 0.3)'
    },

    '&:nth-of-type(1)': { animationDelay: '0s' },
    '&:nth-of-type(2)': { animationDelay: '0.4s' },
    '&:nth-of-type(3)': { animationDelay: '0.8s' },
    '&:nth-of-type(4)': { animationDelay: '1.2s' }
  },

  warehouseBoxLarge: {
    width: '24px', height: '24px',
    '&::before': { width: '18px', height: '18px' },
    '&::after': { width: '18px', height: '18px' },
    '&:nth-of-type(1)': { left: '15px', top: '50px' },
    '&:nth-of-type(2)': { left: '45px', top: '45px' },
    '&:nth-of-type(3)': { left: '75px', top: '55px' },
    '&:nth-of-type(4)': { left: '105px', top: '40px' }
  },

  warehouseBoxMedium: {
    width: '18px', height: '18px',
    '&::before': { width: '14px', height: '14px' },
    '&::after': { width: '14px', height: '14px' },
    '&:nth-of-type(1)': { left: '10px', top: '35px' },
    '&:nth-of-type(2)': { left: '32px', top: '30px' },
    '&:nth-of-type(3)': { left: '54px', top: '40px' },
    '&:nth-of-type(4)': { left: '76px', top: '25px' }
  },

  warehouseBoxSmall: {
    width: '12px', height: '12px',
    '&::before': { width: '9px', height: '9px' },
    '&::after': { width: '9px', height: '9px' },
    '&:nth-of-type(1)': { left: '7px', top: '25px' },
    '&:nth-of-type(2)': { left: '22px', top: '22px' },
    '&:nth-of-type(3)': { left: '37px', top: '28px' },
    '&:nth-of-type(4)': { left: '52px', top: '18px' }
  },

  warehouseBoxMini: {
    width: '10px', height: '10px',
    '&::before': { width: '7px', height: '7px' },
    '&::after': { width: '7px', height: '7px' },
    '&:nth-of-type(1)': { left: '5px', top: '18px' },
    '&:nth-of-type(2)': { left: '16px', top: '15px' },
    '&:nth-of-type(3)': { left: '27px', top: '21px' },
    '&:nth-of-type(4)': { left: '38px', top: '12px' }
  },

  particleSystem: {
    position: 'absolute',
    top: '0',
    left: '50%',
    transform: 'translateX(-50%)',
    overflow: 'hidden',
    pointerEvents: 'none'
  },

  particleSystemLarge: { width: '160px', height: '120px' },
  particleSystemMedium: { width: '120px', height: '90px' },
  particleSystemSmall: { width: '90px', height: '70px' },
  particleSystemMini: { width: '70px', height: '50px' },

  particle: {
    position: 'absolute',
    backgroundColor: '#4A90E2',
    borderRadius: '50%',
    opacity: 0.8,
    animation: 'particleFloat 4s linear infinite',
    boxShadow: '0 0 6px rgba(74, 144, 226, 0.5)',

    '&:nth-of-type(1)': { left: '15%', animationDelay: '0s', animationDuration: '3.5s' },
    '&:nth-of-type(2)': { left: '35%', animationDelay: '0.7s', animationDuration: '4s' },
    '&:nth-of-type(3)': { left: '55%', animationDelay: '1.4s', animationDuration: '3.8s' },
    '&:nth-of-type(4)': { left: '75%', animationDelay: '2.1s', animationDuration: '4.2s' },
    '&:nth-of-type(5)': { left: '25%', animationDelay: '2.8s', animationDuration: '3.6s' },
    '&:nth-of-type(6)': { left: '65%', animationDelay: '3.5s', animationDuration: '4.1s' }
  },

  particleLarge: { width: '4px', height: '4px' },
  particleMedium: { width: '3px', height: '3px' },
  particleSmall: { width: '2px', height: '2px' },
  particleMini: { width: '1.5px', height: '1.5px' },

  scannerContainer: {
    position: 'relative',
    background: 'linear-gradient(90deg, transparent 0%, rgba(74, 144, 226, 0.15) 50%, transparent 100%)',
    borderRadius: '12px',
    border: '2px solid #4A90E2',
    overflow: 'hidden',
    marginBottom: theme.spacing(3),
    boxShadow: '0 4px 12px rgba(74, 144, 226, 0.2)'
  },

  scannerContainerLarge: { width: '120px', height: '72px' },
  scannerContainerMedium: { width: '90px', height: '54px' },
  scannerContainerSmall: { width: '70px', height: '42px' },
  scannerContainerMini: { width: '50px', height: '30px' },

  scannerLine: {
    position: 'absolute',
    width: '3px',
    height: '100%',
    background: 'linear-gradient(to bottom, transparent, #4A90E2, #357ABD, #4A90E2, transparent)',
    animation: 'scannerMove 2.5s ease-in-out infinite',
    boxShadow: '0 0 15px #4A90E2, 0 0 30px rgba(74, 144, 226, 0.5)'
  },

  scannerGrid: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(90deg, rgba(74, 144, 226, 0.1) 1px, transparent 1px),
      linear-gradient(rgba(74, 144, 226, 0.1) 1px, transparent 1px)
    `,
    backgroundSize: '12px 12px',
    opacity: 0.4
  },

  gearContainer: {
    position: 'relative',
    marginBottom: theme.spacing(3)
  },

  gearContainerLarge: { width: '100px', height: '100px' },
  gearContainerMedium: { width: '75px', height: '75px' },
  gearContainerSmall: { width: '50px', height: '50px' },
  gearContainerMini: { width: '32px', height: '32px' },

  gear: {
    background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
    borderRadius: '50%',
    position: 'relative',
    animation: 'gearRotate 3s linear infinite',
    boxShadow: '0 4px 12px rgba(44, 62, 80, 0.3)'
  },

  progressContainer: {
    background: 'rgba(44, 62, 80, 0.1)',
    borderRadius: '6px',
    overflow: 'hidden',
    position: 'relative',
    marginBottom: theme.spacing(2),
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
  },

  progressContainerLarge: { width: '240px', height: '8px' },
  progressContainerMedium: { width: '180px', height: '6px' },
  progressContainerSmall: { width: '120px', height: '4px' },
  progressContainerMini: { width: '80px', height: '3px' },

  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #4A90E2, #357ABD, #4A90E2)',
    borderRadius: '6px',
    animation: 'progressMove 2.5s ease-in-out infinite',
    backgroundSize: '200% 100%'
  },

  progressGlow: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(74, 144, 226, 0.6), transparent)',
    animation: 'progressGlow 2.5s ease-in-out infinite'
  },

  loadingText: {
    color: '#101113ff',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textAlign: 'center',
    animation: 'textPulse 2.5s ease-in-out infinite',
    marginBottom: theme.spacing(1)
  },

  loadingTextLarge: { fontSize: '18px' },
  loadingTextMedium: { fontSize: '16px' },
  loadingTextSmall: { fontSize: '14px' },
  loadingTextMini: { fontSize: '12px' },

  subText: {
    color: '#6B7280',
    fontWeight: 400,
    letterSpacing: '0.3px',
    textAlign: 'center',
    animation: 'textFade 3.5s ease-in-out infinite'
  },

  subTextLarge: { fontSize: '14px' },
  subTextMedium: { fontSize: '12px' },
  subTextSmall: { fontSize: '11px' },
  subTextMini: { fontSize: '10px' },

  inlineText: {
    fontSize: '14px',
    color: '#2c3e50',
    fontWeight: 500,
    whiteSpace: 'nowrap'
  }
}));

const globalStyles = `
@keyframes warehouseFloat {
    0%, 100% { transform: translateY(0px) rotateX(0deg) rotateY(0deg); }
    25% { transform: translateY(-12px) rotateX(2deg) rotateY(1deg); }
    50% { transform: translateY(-8px) rotateX(0deg) rotateY(-1deg); }
    75% { transform: translateY(-15px) rotateX(-2deg) rotateY(0deg); }
}

@keyframes boxStack {
    0%, 100% { transform: translateY(0px) scale(1) rotateZ(0deg); }
    33% { transform: translateY(-10px) scale(1.02) rotateZ(1deg); }
    66% { transform: translateY(-5px) scale(0.98) rotateZ(-1deg); }
}

@keyframes particleFloat {
    0% { 
        transform: translateY(120px) translateX(0px) scale(0) rotate(0deg);
        opacity: 0;
    }
    5% { 
        opacity: 0.8;
        transform: translateY(110px) translateX(8px) scale(1) rotate(45deg);
    }
    95% { 
        opacity: 0.8;
        transform: translateY(-15px) translateX(-8px) scale(1) rotate(315deg);
    }
    100% { 
        transform: translateY(-25px) translateX(0px) scale(0) rotate(360deg);
        opacity: 0;
    }
}

@keyframes scannerMove {
    0% { left: -3px; opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { left: calc(100% + 3px); opacity: 0; }
}

@keyframes gearRotate {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@keyframes progressMove {
    0% { 
        width: 0%;
        background-position: 0% 50%;
        opacity: 0.7;
    }
    50% { 
        width: 75%;
        background-position: 100% 50%;
        opacity: 1;
    }
    100% { 
        width: 100%;
        background-position: 200% 50%;
        opacity: 0.7;
    }
}

@keyframes progressGlow {
    0% { transform: translateX(-100%); opacity: 0; }
    50% { transform: translateX(0%); opacity: 1; }
    100% { transform: translateX(100%); opacity: 0; }
}

@keyframes textPulse {
    0%, 100% { opacity: 0.8; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.03); }
}

@keyframes textFade {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 0.9; }
}
`;

if (typeof document !== 'undefined') {
  const styleId = 'loader-animations-enhanced';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = globalStyles;
    document.head.appendChild(style);
  }
}

type LoaderVariant = 'fullPage' | 'overlay' | 'section' | 'compact' | 'inline' | 'centered';
type LoaderType = 'warehouse' | 'scanner' | 'gear' | 'minimal';
type LoaderSize = 'large' | 'medium' | 'small' | 'mini';

interface LoaderProps {
  variant?: LoaderVariant;
  type?: LoaderType;
  size?: LoaderSize;
  message?: string;
  subMessage?: string;
  showProgress?: boolean;
  className?: string;
  fadeIn?: boolean;
}

const Loader: React.FC<LoaderProps> = ({
  variant = 'fullPage',
  type = 'warehouse',
  size = 'large',
  message = 'Loading Data...',
  subMessage = 'Please wait while we process your request',
  showProgress = true,
  className,
  fadeIn = true
}) => {
  const { classes } = useStyles();

  const getContainerClass = () => {
    const baseClasses = [className].filter(Boolean);

    switch (variant) {
      case 'fullPage':
        return [classes.loaderContainerFullPage, ...baseClasses].join(' ');
      case 'overlay':
        return [classes.loaderContainerOverlay, ...baseClasses].join(' ');
      case 'section':
        return [classes.loaderContainerSection, ...baseClasses].join(' ');
      case 'compact':
        return [classes.loaderContainerCompact, ...baseClasses].join(' ');
      case 'inline':
        return [classes.loaderContainerInline, ...baseClasses].join(' ');
      case 'centered':
        return [classes.loaderContainerCentered, ...baseClasses].join(' ');
      default:
        return [classes.loaderContainerFullPage, ...baseClasses].join(' ');
    }
  };

  const getSizeClasses = () => {
    const sizeMap = {
      warehouse: {
        scene: {
          large: classes.warehouseSceneLarge,
          medium: classes.warehouseSceneMedium,
          small: classes.warehouseSceneSmall,
          mini: classes.warehouseSceneMini
        },
        box: {
          large: classes.warehouseBoxLarge,
          medium: classes.warehouseBoxMedium,
          small: classes.warehouseBoxSmall,
          mini: classes.warehouseBoxMini
        },
        particles: {
          large: classes.particleSystemLarge,
          medium: classes.particleSystemMedium,
          small: classes.particleSystemSmall,
          mini: classes.particleSystemMini
        },
        particle: {
          large: classes.particleLarge,
          medium: classes.particleMedium,
          small: classes.particleSmall,
          mini: classes.particleMini
        }
      },
      scanner: {
        container: {
          large: classes.scannerContainerLarge,
          medium: classes.scannerContainerMedium,
          small: classes.scannerContainerSmall,
          mini: classes.scannerContainerMini
        }
      },
      gear: {
        container: {
          large: classes.gearContainerLarge,
          medium: classes.gearContainerMedium,
          small: classes.gearContainerSmall,
          mini: classes.gearContainerMini
        }
      },
      progress: {
        large: classes.progressContainerLarge,
        medium: classes.progressContainerMedium,
        small: classes.progressContainerSmall,
        mini: classes.progressContainerMini
      },
      text: {
        large: classes.loadingTextLarge,
        medium: classes.loadingTextMedium,
        small: classes.loadingTextSmall,
        mini: classes.loadingTextMini
      },
      subText: {
        large: classes.subTextLarge,
        medium: classes.subTextMedium,
        small: classes.subTextSmall,
        mini: classes.subTextMini
      }
    };

    return sizeMap;
  };

  const sizeClasses = getSizeClasses();

  const renderLoader = () => {
    const isInline = variant === 'inline';

    switch (type) {
      case 'warehouse':
        return (
          <>
            <div className={`${classes.warehouseScene} ${sizeClasses.warehouse.scene[size]}`}>
              <div className={classes.warehouse3D}>
                <div className={`${classes.warehouseBox} ${sizeClasses.warehouse.box[size]}`}></div>
                <div className={`${classes.warehouseBox} ${sizeClasses.warehouse.box[size]}`}></div>
                <div className={`${classes.warehouseBox} ${sizeClasses.warehouse.box[size]}`}></div>
                <div className={`${classes.warehouseBox} ${sizeClasses.warehouse.box[size]}`}></div>
              </div>
              {!isInline && (
                <div className={`${classes.particleSystem} ${sizeClasses.warehouse.particles[size]}`}>
                  <div className={`${classes.particle} ${sizeClasses.warehouse.particle[size]}`}></div>
                  <div className={`${classes.particle} ${sizeClasses.warehouse.particle[size]}`}></div>
                  <div className={`${classes.particle} ${sizeClasses.warehouse.particle[size]}`}></div>
                  <div className={`${classes.particle} ${sizeClasses.warehouse.particle[size]}`}></div>
                  <div className={`${classes.particle} ${sizeClasses.warehouse.particle[size]}`}></div>
                  <div className={`${classes.particle} ${sizeClasses.warehouse.particle[size]}`}></div>
                </div>
              )}
            </div>
          </>
        );

      case 'scanner':
        return (
          <div className={`${classes.scannerContainer} ${sizeClasses.scanner.container[size]}`}>
            <div className={classes.scannerGrid}></div>
            <div className={classes.scannerLine}></div>
          </div>
        );

      case 'gear':
      case 'minimal':
        return (
          <div className={`${classes.gearContainer} ${sizeClasses.gear.container[size]}`}>
            <div className={`${classes.gear}`} style={{
              width: '100%',
              height: '100%'
            }}></div>
          </div>
        );

      default:
        return null;
    }
  };

  const shouldShowProgress = showProgress && variant !== 'inline';
  const shouldShowText = (message || subMessage) && variant !== 'inline';
  const isInline = variant === 'inline';

  const content = (
    <div className={getContainerClass()}>
      {renderLoader()}

      {shouldShowProgress && (
        <div className={`${classes.progressContainer} ${sizeClasses.progress[size]}`}>
          <div className={classes.progressBar}></div>
          <div className={classes.progressGlow}></div>
        </div>
      )}

      {shouldShowText && (
        <>
          <Typography className={`${classes.loadingText} ${sizeClasses.text[size]}`}>
            {message}
          </Typography>

          {subMessage && (
            <Typography className={`${classes.subText} ${sizeClasses.subText[size]}`}>
              {subMessage}
            </Typography>
          )}
        </>
      )}

      {isInline && message && (
        <Typography className={classes.inlineText}>{message}</Typography>
      )}
    </div>
  );

  if (fadeIn) {
    return (
      <Fade in={true} timeout={300}>
        {content}
      </Fade>
    );
  }

  return content;
};

export default Loader;